// src/hooks/useIsAdminReliable.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseBrowser as supabase } from '@/lib/supabase-browser';
import { useAuthSession } from '@/hooks/useAuthSession';

/**
 * useIsAdminReliable — простая и надёжная проверка админских прав
 * на основе session.roles без сетевых вызовов и console логов.
 *
 * Источник правды: useSession.roles[]
 * Возвращает единый интерфейс для всех админских проверок
 *
 * Правила:
 *   - Никаких console.* (0)
 *   - Никаких сетевых вызовов (single source of truth = session)
 *   - Простая и быстрая логика
 *   - Emergency bypass для критических случаев
 */

// Emergency admin bypass - guaranteed access for production emergencies
// SSOT: root@supabank.xyz is the only administrative email
const EMERGENCY_ADMIN_EMAILS = [
  'root@supabank.xyz'
] as const;

const ADMIN_CACHE_PREFIX = 'admin-status:';
const ADMIN_CACHE_TTL_MS = 60_000;
const ADMIN_RPC_TIMEOUT_MS = 3_500;

type AdminCacheEntry = {
  value: boolean;
  expiresAt: number;
};

export type UseIsAdminReliableResult = {
  isAdmin: boolean;
  canPerformAdminActions: boolean;
  isLoading: boolean;
  userId?: string | null;
  failureCount: number;
  lastErrorCode: string | null;
  retry: () => void;
};

type UseIsAdminReliableOptions = {
  forceEnable?: boolean;
};

function checkEmergencyAdminBypass(email?: string | null): boolean {
  if (!email) return false;
  const normalized = email.toLowerCase().trim();
  return EMERGENCY_ADMIN_EMAILS.some(adminEmail => adminEmail === normalized);
}

function checkAdminFromRoles(roles: string[]): boolean {
  return roles.includes('admin') || roles.includes('super_admin') || roles.includes('root');
}

function extractRolesFromSession(session: any): string[] {
  if (!session?.user) return [];

  const user = session.user;
  const appMeta = user.app_metadata || {};
  const userMeta = user.user_metadata || {};
  const roles: string[] = [];

  // Support isAdmin boolean flag (legacy pattern)
  if (appMeta.isAdmin === true) roles.push('admin');
  if (userMeta.isAdmin === true) roles.push('admin');

  // Support roles array
  if (Array.isArray(appMeta.roles)) roles.push(...appMeta.roles);
  if (Array.isArray(userMeta.roles)) roles.push(...userMeta.roles);

  // Support single role string
  if (typeof appMeta.role === 'string' && appMeta.role) roles.push(appMeta.role);

  return [...new Set(roles)];
}

function getCacheKey(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `${ADMIN_CACHE_PREFIX}${userId}`;
}

function readCachedAdminStatus(userId: string | null | undefined): boolean | null {
  if (typeof window === 'undefined') return null;
  const cacheKey = getCacheKey(userId);
  if (!cacheKey) return null;

  try {
    const raw = window.sessionStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as AdminCacheEntry;
    if (typeof parsed?.expiresAt !== 'number' || typeof parsed?.value !== 'boolean') {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    if (parsed.expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    return parsed.value;
  } catch {
    return null;
  }
}

function writeCachedAdminStatus(userId: string | null | undefined, value: boolean): void {
  if (typeof window === 'undefined') return;
  const cacheKey = getCacheKey(userId);
  if (!cacheKey) return;

  try {
    const payload: AdminCacheEntry = {
      value,
      expiresAt: Date.now() + ADMIN_CACHE_TTL_MS,
    };
    window.sessionStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch {
    // Ignore storage errors (quota, disabled storage, etc.)
  }
}

type AdminState = Omit<UseIsAdminReliableResult, 'retry'>;

export function useIsAdminReliable(options: UseIsAdminReliableOptions = {}): UseIsAdminReliableResult {
  const { session, isLoading: sessionLoading, isAuthenticated } = useAuthSession();
  const { forceEnable = true } = options;

  const [state, setState] = useState<AdminState>({
    isAdmin: false,
    canPerformAdminActions: false,
    isLoading: true,
    userId: null,
    failureCount: 0,
    lastErrorCode: null,
  });
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    // E2E Testing Bypass - check cookies first, then localStorage
    if (typeof window !== 'undefined') {
      const getCookieValue = (name: string): string | null => {
        if (typeof document === 'undefined') return null;
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
          const [cookieName, cookieValue] = cookie.trim().split('=');
          if (cookieName === name) return cookieValue || null;
        }
        return null;
      };

      const hasE2EBypass =
        getCookieValue('e2e_admin_bypass') === 'true' ||
        window.localStorage.getItem('e2e_admin_bypass') === 'true';

      if (hasE2EBypass) {
        const e2eEmail = getCookieValue('e2e_user_email') || window.localStorage.getItem('e2e_user_email') || 'admin@test.com';
        setState({
          isAdmin: true,
          canPerformAdminActions: true,
          isLoading: false,
          userId: 'e2e-test-user',
          failureCount: 0,
          lastErrorCode: null,
        });
        return;
      }
    }

    if (!forceEnable) {
      setState({
        isAdmin: false,
        canPerformAdminActions: false,
        isLoading: false,
        userId: null,
        failureCount: 0,
        lastErrorCode: null,
      });
      return;
    }

    if (sessionLoading) {
      setState(prev => ({
        ...prev,
        isLoading: true,
      }));
      return;
    }

    if (!isAuthenticated || !session) {
      setState({
        isAdmin: false,
        canPerformAdminActions: false,
        isLoading: false,
        userId: null,
        failureCount: 0,
        lastErrorCode: null,
      });
      return;
    }

    if (typeof window === 'undefined') {
      setState({
        isAdmin: false,
        canPerformAdminActions: false,
        isLoading: false,
        userId: session.user?.id ?? null,
        failureCount: 0,
        lastErrorCode: 'NO_WINDOW',
      });
      return;
    }

    const userId = session.user?.id ?? null;
    const userEmail = session.user?.email ?? null;
    const hasEmergencyBypass = checkEmergencyAdminBypass(userEmail);
    const roles = extractRolesFromSession(session);
    const hasAdminRole = checkAdminFromRoles(roles);
    const optimisticAdmin = hasEmergencyBypass || hasAdminRole;

    if (hasEmergencyBypass) {
      writeCachedAdminStatus(userId, true);
      setState({
        isAdmin: true,
        canPerformAdminActions: true,
        isLoading: false,
        userId,
        failureCount: 0,
        lastErrorCode: null,
      });
      return;
    }

    const cached = readCachedAdminStatus(userId);
    if (cached !== null) {
      setState({
        isAdmin: cached,
        canPerformAdminActions: cached,
        isLoading: false,
        userId,
        failureCount: 0,
        lastErrorCode: null,
      });
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), ADMIN_RPC_TIMEOUT_MS);

    setState({
      isAdmin: optimisticAdmin,
      canPerformAdminActions: optimisticAdmin,
      isLoading: true,
      userId,
      failureCount: 0,
      lastErrorCode: null,
    });

    (async () => {
      try {
        const { data, error } = await supabase.rpc('auth_is_admin', {}, { signal: controller.signal });
        if (cancelled) return;

        if (error) {
          setState(prev => ({
            isAdmin: optimisticAdmin,
            canPerformAdminActions: optimisticAdmin,
            isLoading: false,
            userId,
            failureCount: prev.failureCount + 1,
            lastErrorCode: error.code ?? error.message ?? 'RPC_ERROR',
          }));
          return;
        }

        const resolvedAdmin = Boolean(data);
        writeCachedAdminStatus(userId, resolvedAdmin);
        setState({
          isAdmin: resolvedAdmin,
          canPerformAdminActions: resolvedAdmin,
          isLoading: false,
          userId,
          failureCount: 0,
          lastErrorCode: null,
        });
      } catch (err) {
        if (cancelled) return;

        const isAbortError = err instanceof DOMException && err.name === 'AbortError';
        setState(prev => ({
          isAdmin: optimisticAdmin,
          canPerformAdminActions: optimisticAdmin,
          isLoading: false,
          userId,
          failureCount: prev.failureCount + 1,
          lastErrorCode: isAbortError ? 'RPC_TIMEOUT' : 'RPC_EXCEPTION',
        }));
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [forceEnable, sessionLoading, isAuthenticated, session, refreshToken]);

  const retry = useCallback(() => {
    setRefreshToken(value => value + 1);
  }, []);

  return useMemo(() => ({
    ...state,
    retry,
  }), [state, retry]);
}

// Backwards compatibility aliases
export function useIsAdminReliableState(options: UseIsAdminReliableOptions = {}): UseIsAdminReliableResult {
  return useIsAdminReliable(options);
}

// Kill-switch for backwards compatibility
export const enableAdminChecks = { current: true };

export default useIsAdminReliable;